/**
 * A categoria e o tipo do veículo, na ficha do veículo.
 *
 * **A categoria decide se o cliente consegue abrir o conteúdo.** `PermiteAcesso`
 * exige uma linha casando a licença com a categoria deste veículo; sem categoria
 * o `EXISTS` é falso e **nenhum contrato alcança o veículo**. Por isso o painel
 * grita quando está vazio — e grita mais alto quando há conteúdo publicado
 * atrás, porque aí o item já aparece na busca do cliente e a abertura falha
 * calada.
 *
 * **O tipo vem junto e sem alarme.** Hatch, Sedã, SUV, Utilitário não entram em
 * regra de acesso nenhuma. São 1.175 dos 1.202 veículos sem tipo — ou o campo
 * morreu, ou nunca foi preenchido; de qualquer forma não prende conteúdo de
 * ninguém. Entra porque é o mesmo gesto na mesma ficha, mas avisar sobre o que
 * não machuca é o jeito de ensinar alguém a ignorar aviso.
 *
 * **Caixas, e não incluir/excluir um por vez.** O sistema antigo tem um `select`
 * mais dois botões por linha; a API recebe o conjunto inteiro. Marcar tudo o que
 * vale e gravar uma vez é menos cliques e não deixa meia alteração no ar.
 */

import { useEffect, useState } from "react"
import {
  usarCategoriasDoVeiculo, usarSalvarAlcance, usarTiposDoVeiculo,
  type OpcaoDoVeiculo,
} from "@/api/alcanceDoVeiculo"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"

export function AlcanceDoVeiculo({ veiculoId, aoAvisar }: {
  veiculoId: number
  aoAvisar: (m: string | null) => void
}) {
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line2)" }}>
      <Conjunto
        escopo="categorias"
        titulo="Categorias"
        veiculoId={veiculoId}
        aoAvisar={aoAvisar}
        explicacao={
          <>
            É o que liga o veículo ao contrato: o cliente só abre o conteúdo se a licença dele
            assinar <b>alguma destas</b>. Sem nenhuma marcada, <b>ninguém abre</b>.
          </>
        }
        aviso={
          <>
            <b>Este veículo está fora de todo contrato.</b> Qualquer conteúdo publicado nele
            aparece na busca do cliente e não abre, sem mensagem nenhuma explicando.
          </>
        }
      />
      <Conjunto
        escopo="tipos"
        titulo="Tipos"
        veiculoId={veiculoId}
        aoAvisar={aoAvisar}
        explicacao={
          <>
            Carroceria, para catálogo. <b>Não entra em regra de acesso</b> — 1.175 dos 1.202
            veículos estão sem, e isso não prende conteúdo de ninguém.
          </>
        }
      />
    </div>
  )
}

function Conjunto({ escopo, titulo, veiculoId, explicacao, aviso, aoAvisar }: {
  escopo: "categorias" | "tipos"
  titulo: string
  veiculoId: number
  explicacao: React.ReactNode
  /** Só a categoria tem: o vazio dela é defeito, o do tipo não é. */
  aviso?: React.ReactNode
  aoAvisar: (m: string | null) => void
}) {
  const consulta = escopo === "categorias"
    ? usarCategoriasDoVeiculo(veiculoId)
    : usarTiposDoVeiculo(veiculoId)
  const salvar = usarSalvarAlcance(veiculoId, escopo)

  const [marcados, setMarcados] = useState<Set<number> | null>(null)

  // O que veio do servidor é a verdade; a edição local só existe entre carregar
  // e gravar. Trocar de veículo tem de zerar, ou a marcação do anterior vaza.
  useEffect(() => { setMarcados(null) }, [veiculoId, escopo])

  const doServidor = consulta.data
  const atuais = doServidor ? new Set(doServidor.filter((o) => o.tem).map((o) => o.id)) : null
  const agora = marcados ?? atuais

  if (consulta.isPending) {
    return <Moldura titulo={titulo}>
      <p style={vazio}>Carregando…</p>
    </Moldura>
  }
  if (consulta.error || !doServidor || !agora || !atuais) {
    return <Moldura titulo={titulo}>
      <p style={{ ...vazio, color: "var(--erro)" }}>
        {consulta.error instanceof ErroDaApi ? consulta.error.message : "Não foi possível ler."}
      </p>
    </Moldura>
  }

  const mudou = agora.size !== atuais.size || [...agora].some((id) => !atuais.has(id))

  function alternar(o: OpcaoDoVeiculo) {
    const proximo = new Set(agora!)
    if (proximo.has(o.id)) proximo.delete(o.id)
    else proximo.add(o.id)
    setMarcados(proximo)
  }

  return (
    <Moldura titulo={titulo}>
      <p style={{ margin: "0 0 7px", fontSize: 10.5, color: "var(--txt3)", lineHeight: 1.5 }}>
        {explicacao}
      </p>

      {/* O alarme lê o servidor, e não a marcação local: ele descreve o que é
          verdade em produção agora. Sumir ao marcar a caixa diria "resolvido"
          antes de gravar — e o par Gravar/Desfazer logo abaixo já deixa claro
          que há alteração pendente. */}
      {aviso && atuais.size === 0 && (
        <p style={{
          margin: "0 0 8px", padding: "6px 9px", borderRadius: 5, fontSize: 11, lineHeight: 1.5,
          background: "color-mix(in srgb, var(--erro) 10%, transparent)",
          border: "1px solid var(--erro)", color: "var(--txt2)",
        }}>
          {aviso}
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {doServidor.map((o) => (
          <label key={o.id} style={{
            display: "flex", alignItems: "center", gap: 7, fontSize: 12, cursor: "pointer",
          }}>
            <input type="checkbox" checked={agora.has(o.id)} onChange={() => alternar(o)} />
            <span>{o.nome}</span>
          </label>
        ))}
      </div>

      {mudou && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 9 }}>
          <button
            onClick={() => {
              aoAvisar(null)
              salvar.mutate([...agora], {
                onSuccess: () => {
                  setMarcados(null)
                  aoAvisar(`${titulo} do veículo ${veiculoId} gravados.`)
                },
                onError: (e) => aoAvisar(
                  e instanceof ErroDeTrava || e instanceof ErroDaApi
                    ? e.message
                    : "Não foi possível gravar."),
              })
            }}
            disabled={salvar.isPending}
            style={{
              border: "1px solid var(--acao)", background: "var(--acao)", color: "#fff",
              borderRadius: 6, padding: "4px 11px", fontSize: 12,
              cursor: salvar.isPending ? "default" : "pointer",
            }}
          >
            {salvar.isPending ? "Gravando…" : "Gravar"}
          </button>
          <button
            onClick={() => setMarcados(null)}
            style={{
              border: "1px solid var(--line)", background: "var(--surface)", color: "var(--txt2)",
              borderRadius: 6, padding: "4px 11px", fontSize: 12, cursor: "pointer",
            }}
          >
            Desfazer
          </button>
        </div>
      )}
    </Moldura>
  )
}

function Moldura({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h3 style={{
        margin: "0 0 5px", fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
        textTransform: "uppercase", color: "var(--acao)",
      }}>
        {titulo}
      </h3>
      {children}
    </div>
  )
}

const vazio: React.CSSProperties = { margin: 0, fontSize: 11.5, color: "var(--txt3)" }
